// App
import { shallowEqual, useSelector } from 'react-redux';

// Constellation
import { IconChevronLeftOutline, TextButton } from '@zillow/constellation';

import pathsManifest from '../../utils/pathsManifest';

// Components
import ResponsiveHeading from '../../StyledComponents/ResponsiveText';
import ZestimateCard from '../../components/ZestimateCard';

import HeroImageWebp from 'images/priceMyRentalHero.webp';

import * as S from './styles';

// Types
import type React from 'react';
import type { State } from '../../types/State';

const HeadingCard = () => {
  const { fromApp, priceFromFilter, userProperty, min, max } = useSelector(
    (state: State) => ({
      fromApp: state.app.fromApp,
      priceFromFilter: state.filter.price,
      userProperty: state.address,
      min: state.comparables.min,
      max: state.comparables.max,
    }),
    shallowEqual,
  );

  const {
    street,
    beds,
    baths,
    sqft,
    city,
    state,
    zip,
    unit,
    rentZestimate,
    rentZestimateRangeHigh,
    rentZestimateRangeLow,
    zpid,
  } = userProperty;
  const hasUserPropertyData = street !== null;

  const h1Text = hasUserPropertyData ? (
    <>
      {street} {unit && unit !== ' ' ? ', Unit ' + unit : ''}
    </>
  ) : (
    <>
      {' '}
      <br />
      Address not found
    </>
  );

  const h2Text = hasUserPropertyData ? (
    <>
      {city}, {state} {zip}
      <br />
      {beds !== 0 ? <>{beds} bed</> : <>Studio</>} • {baths} bath • {sqft} Sq Ft
    </>
  ) : (
    ''
  );

  const styles = {
    // Simulate overlay on background. Inline styles prevents re-downloading of images
    background: {
      background: `linear-gradient(rgba(47, 60, 83, 0.8), rgba(47, 60, 83, 0.8)), url(${HeroImageWebp})`,
    } as React.CSSProperties,
  };

  // Prioritize rent zestimate range from property details API response.
  // In cases where it might not exist, fall back to previous rent range comparable.
  const getMinMaxRentRange = () => {
    return {
      min: rentZestimateRangeLow ? rentZestimateRangeLow : min,
      max: rentZestimateRangeHigh ? rentZestimateRangeHigh : max,
    };
  };

  return (
    <S.Container style={styles.background}>
      <S.Details>
        <ResponsiveHeading fontType="h3" level="1" fontColor="textWhite">
          {h1Text}
        </ResponsiveHeading>

        {h2Text && (
          <ResponsiveHeading fontType="body" level="2" fontColor="textWhite">
            {h2Text}
          </ResponsiveHeading>
        )}

        <TextButton
          as="a"
          href={`${pathsManifest.PriceMyRentalPage}/${fromApp ? '?fromApp=true' : ''}`}
          inverse
          fontType="bodySmall"
          marginTop="md"
          style={{ color: 'white' }}
        >
          <IconChevronLeftOutline /> Try another address
        </TextButton>
      </S.Details>
      {hasUserPropertyData && (rentZestimate || priceFromFilter) && (
        <ZestimateCard
          min={getMinMaxRentRange().min}
          max={getMinMaxRentRange().max}
          rentZestimate={rentZestimate || null}
          zpid={zpid || null}
          priceFromFilter={priceFromFilter}
        />
      )}
    </S.Container>
  );
};

export default HeadingCard;
